import { createApiClient } from './client'
import {
  CreateTripDto,
  CreateQuickTripDto,
  UpdateTripDto,
  AddMemberDto,
  AddTagAlongDto,
  UpdateMemberRoleDto,
  TripQueryDto,
  TripResponse,
  TripWithDetailsResponse,
  MemberResponse,
  PaginatedResponse,
  GuestPermissions,
  UpdateGuestPermissionsDto,
  GuestAccessRequest,
  CreateAccessRequestDto,
  TripWeatherLocation,
  CampspotterAssessment
} from './types'
import {
  CreateWaypointDto,
  UpdateWaypointDto,
  WaypointResponse
} from './routes'

// Day Summary types
export interface DaySummary {
  day: number
  date: string
  formattedDate: string
  meetup?: {
    location: string | null
    time: string | null
    coordinates?: {
      latitude: number
      longitude: number
    } | null
  }
  routes: Array<{
    id: string
    name: string
    distance: number | null
    duration: number | null
    start_time: string | null
    end_time: string | null
    waypoints: Array<{
      id: string
      name: string
      latitude: number | null
      longitude: number | null
      estimated_arrival: string | null
      estimated_departure: string | null
      order_index: number | null
    }>
  }>
  meals: {
    breakfast: Array<any>
    lunch: Array<any>
    dinner: Array<any>
    snack: Array<any>
  }
  totalDistance: number
  totalDuration: number
  waypointCount: number
  routeCount: number
  mealCount: number
}

export interface DaySummaryResponse {
  trip_id: string
  trip_name: string
  start_date: string | null
  end_date: string | null
  days: DaySummary[]
}

export interface TripFriendItem {
  id: string
  full_name: string | null
  email: string | null
  profile_image_path: string | null
  invite_status: 'pending' | null
}

export interface PendingTripInviteItem {
  id: string
  trip_id: string
  trip_name: string
  start_date: string | null
  end_date: string | null
  inviter_name: string
  created_at: string
}

// Trips API class
export class TripsApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  // Unified create (manual mode)
  async createTripUnified(payload: { mode: 'manual'; data: CreateTripDto }): Promise<any> {
    const response = await this.apiClient.post<{ success: boolean; data: any }>('/trips/create', payload)
    return response.data
  }

  // Trip CRUD operations
  async createTrip(tripData: CreateTripDto): Promise<TripResponse> {
    const response = await this.apiClient.post<{ success: boolean; data: TripResponse }>('/trips', tripData)
    return response.data
  }

  async createQuickSpontaneousTrip(quickTripData: CreateQuickTripDto): Promise<TripResponse> {
    const response = await this.apiClient.post<{ success: boolean; data: TripResponse }>('/trips/quick-create', quickTripData)
    return response.data
  }

  async getTrips(query?: TripQueryDto & { includeArchived?: boolean; archivedOnly?: boolean; sort_by?: string; sort_order?: string }): Promise<PaginatedResponse<TripResponse>> {
    const queryParams: any = { ...query }
    if (queryParams.includeArchived !== undefined) {
      queryParams.include_archived = queryParams.includeArchived.toString()
      delete queryParams.includeArchived
    }
    if (queryParams.archivedOnly !== undefined) {
      queryParams.archived_only = queryParams.archivedOnly.toString()
      delete queryParams.archivedOnly
    }
    // sort_by and sort_order are already in snake_case, keep them as is
    if (queryParams.sort_by) {
      queryParams.sort_by = queryParams.sort_by
    }
    if (queryParams.sort_order) {
      queryParams.sort_order = queryParams.sort_order
    }
    const response = await this.apiClient.get<{ success: boolean; data: TripResponse[]; pagination: any }>('/trips', queryParams)
    return {
      data: response.data,
      pagination: response.pagination
    }
  }

  async getTrip(tripId: string): Promise<TripWithDetailsResponse> {
    // Use no-store so refetches (e.g. after role/owner changes) always get fresh data; avoids stale UI from HTTP cache
    const response = await this.apiClient.get<{ success: boolean; data: TripWithDetailsResponse }>(`/trips/${tripId}`, undefined, { cache: 'no-store' })
    return response.data
  }

  async getTripAsGuest(tripId: string, inviteCode: string, guestName: string, accessToken?: string): Promise<TripWithDetailsResponse> {
    const params: any = {}
    
    if (accessToken) {
      // Use access token method
      params.accessToken = accessToken
    } else {
      // Fall back to invite code method
      params.inviteCode = inviteCode
      params.guestName = guestName
    }
    
    const response = await this.apiClient.get<{ success: boolean; data: TripWithDetailsResponse }>(`/trips/${tripId}/guest`, params)
    return response.data
  }

  // Guest Permissions API methods
  async getGuestPermissions(tripId: string): Promise<GuestPermissions> {
    const response = await this.apiClient.get<{ success: boolean; data: GuestPermissions }>(`/trips/${tripId}/guest-permissions`)
    return response.data
  }

  async updateGuestPermissions(tripId: string, permissions: UpdateGuestPermissionsDto): Promise<GuestPermissions> {
    const response = await this.apiClient.put<{ success: boolean; data: GuestPermissions }>(`/trips/${tripId}/guest-permissions`, permissions)
    return response.data
  }

  // Guest Access Request API methods
  async requestGuestAccess(tripId: string, data: CreateAccessRequestDto): Promise<GuestAccessRequest> {
    const response = await this.apiClient.post<{ success: boolean; data: GuestAccessRequest }>(`/trips/${tripId}/guest-access-request`, data)
    return response.data
  }

  async getAccessRequests(tripId: string): Promise<GuestAccessRequest[]> {
    const response = await this.apiClient.get<{ success: boolean; data: GuestAccessRequest[] }>(`/trips/${tripId}/access-requests`)
    return response.data
  }

  async respondToAccessRequest(tripId: string, requestId: string, approved: boolean): Promise<GuestAccessRequest> {
    const response = await this.apiClient.post<{ success: boolean; data: GuestAccessRequest }>(`/trips/${tripId}/access-requests/${requestId}/respond`, { approved })
    return response.data
  }

  async getTripInviteFriends(tripId: string): Promise<TripFriendItem[]> {
    const response = await this.apiClient.get<{ success: boolean; data: TripFriendItem[] }>(`/trips/${tripId}/invite-friends`)
    return response.data
  }

  async sendTripInvite(tripId: string, invitedUserId: string): Promise<{ id: string }> {
    const response = await this.apiClient.post<{ success: boolean; data: { id: string } }>(`/trips/${tripId}/invite-friends`, { invited_user_id: invitedUserId })
    return response.data
  }

  async acceptTripInvite(tripId: string): Promise<{ member: unknown }> {
    const response = await this.apiClient.post<{ success: boolean; data: { member: unknown } }>(`/trips/${tripId}/invites/accept`)
    return response.data
  }

  async getMyPendingInvites(): Promise<PendingTripInviteItem[]> {
    const response = await this.apiClient.get<{ success: boolean; data: PendingTripInviteItem[] }>('/trips/invites/pending')
    return response.data ?? []
  }

  async updateTrip(tripId: string, tripData: UpdateTripDto): Promise<TripResponse> {
    const response = await this.apiClient.put<{ success: boolean; data: TripResponse }>(`/trips/${tripId}`, tripData)
    return response.data
  }

  async deleteTrip(tripId: string): Promise<void> {
    await this.apiClient.delete<{ success: boolean; message: string }>(`/trips/${tripId}`)
  }
  
  // Waypoint operations
  async addWaypoint(tripId: string, waypointData: CreateWaypointDto): Promise<WaypointResponse> {
    return this.apiClient.post<WaypointResponse>(`/trips/${tripId}/waypoints`, waypointData)
  }

  async addExistingWaypointToTrip(
    tripId: string,
    waypointId: string,
    options?: {
      trip_day?: number | null
      order_index?: number
    }
  ): Promise<any> {
    return this.apiClient.post<any>(`/trips/${tripId}/waypoints/${waypointId}`, options || {})
  }

  async removeWaypointFromTrip(tripId: string, waypointId: string): Promise<void> {
    return this.apiClient.delete<void>(`/trips/${tripId}/waypoints/${waypointId}`)
  }

  async updateWaypoint(
    tripId: string,
    waypointId: string,
    waypointData: UpdateWaypointDto
  ): Promise<WaypointResponse> {
    // Use trip-specific endpoint for time updates (handles trip_waypoints junction table)
    // For other updates, this endpoint also works but updates waypoints table
    const response = await this.apiClient.put<{ success: boolean; data: WaypointResponse }>(
      `/trips/${tripId}/waypoints/${waypointId}`,
      waypointData
    )
    return response.data
  }

  async updateTripWaypoint(
    tripId: string,
    waypointId: string,
    updates: {
      trip_day?: number
      order_index?: number
      start_time?: string | null
      end_time?: string | null
    }
  ): Promise<WaypointResponse> {
    // Use trip-specific endpoint for junction table updates (trip_day, order_index, time slots)
    // This endpoint specifically handles trip_waypoints junction table fields
    const response = await this.apiClient.put<{ success: boolean; data: WaypointResponse }>(
      `/trips/${tripId}/waypoints/${waypointId}`,
      updates
    )
    return response.data
  }

  async deleteWaypoint(tripId: string, waypointId: string): Promise<void> {
    return this.apiClient.delete<void>(`/trips/waypoints/${waypointId}`)
  }

  async reorderWaypoints(tripId: string, waypointIds: string[]): Promise<void> {
    await this.apiClient.post<void>(`/trips/${tripId}/waypoints/reorder`, {
      waypointIds: waypointIds
    })
  }

  // Member operations
  async addMember(tripId: string, memberData: AddMemberDto): Promise<MemberResponse> {
    const response = await this.apiClient.post<{ success: boolean; data: MemberResponse }>(`/trips/${tripId}/members`, memberData)
    return response.data
  }

  async addTagAlong(tripId: string, parentMemberId: string, data: AddTagAlongDto): Promise<MemberResponse> {
    const response = await this.apiClient.post<{ success: boolean; data: MemberResponse }>(
      `/trips/${tripId}/members/${parentMemberId}/tag-alongs`,
      data
    )
    return response.data
  }

  async updateMemberRole(
    tripId: string,
    memberId: string,
    roleData: UpdateMemberRoleDto
  ): Promise<MemberResponse> {
    return this.apiClient.put<MemberResponse>(
      `/trips/${tripId}/members/${memberId}/role`,
      roleData
    )
  }

  async removeMember(tripId: string, memberId: string): Promise<void> {
    return this.apiClient.delete<void>(`/trips/${tripId}/members/${memberId}`)
  }

  async leaveTrip(tripId: string): Promise<void> {
    return this.apiClient.post<void>(`/trips/${tripId}/leave`)
  }

  // Public trips
  async getPublicTrips(query?: Omit<TripQueryDto, 'is_public'>): Promise<PaginatedResponse<TripResponse>> {
    return this.apiClient.get<PaginatedResponse<TripResponse>>('/trips/public', query)
  }

  // Trip search
  async searchTrips(query: string, filters?: Omit<TripQueryDto, 'search'>): Promise<PaginatedResponse<TripResponse>> {
    return this.apiClient.get<PaginatedResponse<TripResponse>>('/trips/search', {
      ...filters,
      search: query
    })
  }

  // Itinerary suggestions
  async createSuggestion(tripId: string, suggestion: {
    suggestion_type: string
    suggestion_data: any
    original_data?: any
  }): Promise<any> {
    const response = await this.apiClient.post<{ success: boolean; data: any }>(
      `/trips/${tripId}/suggestions`,
      suggestion
    )
    return response.data
  }

  async getSuggestions(tripId: string, filters?: {
    status?: 'pending' | 'approved' | 'rejected'
    suggestion_type?: string
    suggested_by_user_id?: string
  }): Promise<any[]> {
    const response = await this.apiClient.get<{ success: boolean; data: any[] }>(
      `/trips/${tripId}/suggestions`,
      filters
    )
    return response.data
  }

  async getPendingSuggestionsCount(tripId: string): Promise<number> {
    const response = await this.apiClient.get<{ success: boolean; data: { count: number } }>(
      `/trips/${tripId}/suggestions/pending-count`
    )
    return response.data.count
  }

  async approveSuggestion(tripId: string, suggestionId: string, reviewNotes?: string): Promise<any> {
    const response = await this.apiClient.patch<{ success: boolean; data: any }>(
      `/trips/${tripId}/suggestions/${suggestionId}/approve`,
      { review_notes: reviewNotes }
    )
    return response.data
  }

  async rejectSuggestion(tripId: string, suggestionId: string, reviewNotes?: string): Promise<any> {
    const response = await this.apiClient.patch<{ success: boolean; data: any }>(
      `/trips/${tripId}/suggestions/${suggestionId}/reject`,
      { review_notes: reviewNotes }
    )
    return response.data
  }

  // Day summaries
  async getDaySummaries(tripId: string): Promise<DaySummaryResponse> {
    const response = await this.apiClient.get<{ success: boolean; data: DaySummaryResponse }>(
      `/trips/${tripId}/itinerary/summary`
    )
    return response.data
  }

  // Archive operations
  async archiveTrip(tripId: string): Promise<void> {
    await this.apiClient.patch<{ success: boolean; message: string }>(`/trips/${tripId}/archive`)
  }

  async unarchiveTrip(tripId: string): Promise<void> {
    await this.apiClient.patch<{ success: boolean; message: string }>(`/trips/${tripId}/unarchive`)
  }

  async restoreTripFromRetention(tripId: string): Promise<TripResponse> {
    const res = await this.apiClient.patch<{ success: boolean; data: TripResponse }>(
      `/trips/${tripId}/retention/restore`
    )
    return res.data
  }

  // Weather operations
  async getTripWeather(tripId: string): Promise<TripWeatherLocation[]> {
    const response = await this.apiClient.get<{ success: boolean; data: TripWeatherLocation[] }>(
      `/trips/${tripId}/weather`
    )
    return response.data
  }

  /**
   * Manual fetch: call Campspotter for all weather waypoints and return normalized assessments.
   * Pro/Team only.
   */
  async fetchTripWeather(tripId: string): Promise<CampspotterAssessment[]> {
    const response = await this.apiClient.post<{ success: boolean; data: CampspotterAssessment[] }>(
      `/trips/${tripId}/weather/fetch`,
      {}
    )
    return response.data ?? []
  }

  /**
   * Test endpoint: call Campspotter with this trip's first waypoint and dates, return raw response.
   * For testing/debugging only.
   */
  async getCampspotterTest(tripId: string): Promise<{
    success: boolean
    ok?: boolean
    data?: unknown
    request?: { lat: number; lon: number; startDate: string; endDate: string; spotName?: string }
    error?: string
    details?: { status?: number; statusText?: string; errorBody?: string }
  }> {
    const response = await this.apiClient.get<{
      success: boolean
      ok?: boolean
      data?: unknown
      request?: { lat: number; lon: number; startDate: string; endDate: string; spotName?: string }
      error?: string
      details?: { status?: number; statusText?: string; errorBody?: string }
    }>(`/trips/${tripId}/weather/campspotter-test`)
    return response
  }
}
